import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { UsageLogsApi, UsageLogRow } from '@/services/usage-logs.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { cn } from '@/lib/cn';
import { JsonCodeBlock } from './JsonCodeBlock';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

const STATUS_CLASS = (status: number): string => {
  if (status >= 500) return 'text-red-600';
  if (status >= 400) return 'text-amber-600';
  return 'text-green-700';
};

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'endpoints', label: 'Usage by Endpoint' },
  { key: 'never-used', label: 'Never Used' },
  { key: 'logs', label: 'Logs' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Mobile-app API "Usage History" — modeled on a sibling project's
 * (contractor-saas) identical feature: same tabs, same filters, same
 * drill-down shape. Backed by UsageLoggingMiddleware (capture) +
 * UsageLogsService (aggregation) — see docs/DEVIATIONS.md.
 */
export function UsageHistoryPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [dateFrom, setDateFrom] = useState(daysAgoISO(30));
  const [dateTo, setDateTo] = useState(todayISO());
  const [userId, setUserId] = useState('');
  const [routePattern, setRoutePattern] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['usage-logs-summary', { dateFrom, dateTo }],
    queryFn: () => UsageLogsApi.summary({ dateFrom, dateTo }),
  });

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ['usage-logs-logs', { dateFrom, dateTo, userId, routePattern, page }],
    queryFn: () =>
      UsageLogsApi.logs({
        dateFrom,
        dateTo,
        userId: userId || undefined,
        routePattern: routePattern || undefined,
        page,
        limit: 50,
      }),
  });

  const perUser = summary?.perUser ?? [];
  const perEndpoint = summary?.perEndpoint ?? [];
  const unused = summary?.unusedEndpoints ?? [];
  const rows = logs?.data ?? [];
  const totalPages = logs ? Math.max(1, Math.ceil(logs.meta.total / logs.meta.limit)) : 1;

  const routeOptions = useMemo(() => Array.from(new Set(perEndpoint.map((e) => e.routePattern))).sort(), [perEndpoint]);

  function toggle(id: string) {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function resetToPage1<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  return (
    <>
      <PageHeader title="Usage History" description="Who's using the mobile app API, which endpoints, and which have never been hit." />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs text-slate-500">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => resetToPage1(setDateFrom)(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-500">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => resetToPage1(setDateTo)(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>
        <select
          value={userId}
          onChange={(e) => resetToPage1(setUserId)(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Users</option>
          {perUser
            .filter((u) => u.userId)
            .map((u) => (
              <option key={u.userId} value={u.userId ?? ''}>
                {u.userName}
              </option>
            ))}
        </select>
        <select
          value={routePattern}
          onChange={(e) => resetToPage1(setRoutePattern)(e.target.value)}
          className="min-w-[220px] rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Endpoints</option>
          {routeOptions.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={cn(
              'whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium',
              activeTab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatChip label="Total Hits" value={summary?.totalHits ?? 0} />
            <StatChip label="Unique Users" value={summary?.uniqueUsers ?? 0} />
            <StatChip label="Known Endpoints" value={summary?.totalKnownEndpoints ?? 0} />
            <StatChip label="Never Used" value={unused.length} />
          </div>

          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Usage by User</div>
            {summaryLoading ? (
              <div className="px-4 py-6 text-sm text-slate-400">Loading…</div>
            ) : perUser.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-400">No hits in this range.</div>
            ) : (
              perUser.map((u) =>
                u.userId ? (
                  <Link
                    key={u.userId}
                    to={`/usage-history/${u.userId}`}
                    className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 text-sm last:border-0 hover:bg-slate-50"
                  >
                    <div>
                      <div className="font-medium text-brand-700">{u.userName}</div>
                      <div className="text-xs text-slate-500">Last hit {fmtDateTime(u.lastHitAt)}</div>
                    </div>
                    <div className="font-semibold text-slate-900">{u.hits}</div>
                  </Link>
                ) : (
                  <div key="unauth" className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 text-sm last:border-0">
                    <div>
                      <div className="font-medium text-slate-900">{u.userName}</div>
                      <div className="text-xs text-slate-500">Last hit {fmtDateTime(u.lastHitAt)}</div>
                    </div>
                    <div className="font-semibold text-slate-900">{u.hits}</div>
                  </div>
                ),
              )
            )}
          </div>
        </div>
      )}

      {activeTab === 'endpoints' && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Usage by Endpoint</div>
          {summaryLoading ? (
            <div className="px-4 py-6 text-sm text-slate-400">Loading…</div>
          ) : perEndpoint.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-400">No hits in this range.</div>
          ) : (
            perEndpoint.map((e) => (
              <div key={`${e.method} ${e.routePattern}`} className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 text-sm last:border-0">
                <div className="min-w-0">
                  <div className="truncate font-mono text-xs font-medium text-slate-900">
                    <span className="text-brand-600">{e.method}</span> {e.routePattern}
                  </div>
                  <div className="text-xs text-slate-500">
                    avg {e.avgDurationMs}ms · last {fmtDateTime(e.lastHitAt)}
                  </div>
                </div>
                <div className="font-semibold text-slate-900">{e.hits}</div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'never-used' && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Never Used (all time) — {unused.length}
          </div>
          {unused.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-400">Every known endpoint was used at least once.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {unused.map((r) => (
                <div key={`${r.method} ${r.pattern}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <span className="font-mono text-xs text-brand-600">{r.method}</span>
                  <span className="font-mono text-xs text-slate-900">{r.pattern}</span>
                  <span className="text-xs text-slate-500">— {r.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="grid grid-cols-[1.4fr_1.6fr_0.6fr_0.7fr_1fr_0.4fr] gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <span>User</span>
              <span>Endpoint</span>
              <span>Status</span>
              <span>Duration</span>
              <span>Time</span>
              <span className="text-right">Detail</span>
            </div>

            {logsLoading ? (
              <div className="px-4 py-6 text-sm text-slate-400">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-6 text-sm text-slate-400">No requests match these filters.</div>
            ) : (
              rows.map((row) => <LogRow key={row.id} row={row} expanded={expanded.has(row.id)} onToggle={() => toggle(row.id)} />)
            )}
          </div>

          {logs && logs.meta.total > logs.meta.limit && (
            <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
              <span>
                Page {page} of {totalPages} · {logs.meta.total} total requests
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-md border border-slate-200 px-3 py-1.5 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-md border border-slate-200 px-3 py-1.5 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function StatChip({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function LogRow({ row, expanded, onToggle }: { row: UsageLogRow; expanded: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-slate-100 last:border-0">
      <div className="grid grid-cols-[1.4fr_1.6fr_0.6fr_0.7fr_1fr_0.4fr] items-center gap-2 px-4 py-2.5 text-sm">
        <div className="truncate">{row.userName ?? '—'}</div>
        <div className="truncate font-mono text-xs">
          <span className="text-brand-600">{row.method}</span> {row.routePattern}
        </div>
        <div className={cn('font-semibold', STATUS_CLASS(row.statusCode))}>{row.statusCode}</div>
        <div className="text-slate-500">{row.durationMs}ms</div>
        <div className="text-xs text-slate-500">{fmtDateTime(row.createdAt)}</div>
        <div className="text-right">
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-500 hover:border-brand-300"
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <div>
            <div className="mb-1 text-xs font-semibold text-slate-500">Request URL</div>
            <div className="overflow-x-auto rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-900">
              <span className="text-brand-600">{row.method}</span> {row.path}
            </div>
          </div>
          {row.method !== 'GET' && (
            <div>
              <div className="mb-1 text-xs font-semibold text-slate-500">Request</div>
              <JsonCodeBlock json={row.requestBody ?? '(empty)'} />
            </div>
          )}
          <div>
            <div className="mb-1 text-xs font-semibold text-slate-500">Response</div>
            <JsonCodeBlock json={row.responseBody ?? '(empty)'} />
          </div>
        </div>
      )}
    </div>
  );
}
